import { Close, Search } from "@/icons";
import { cn } from "@/lib/cn";
import { forwardRef } from "react";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { TextField, type TextFieldProps } from "./TextField";

/** Props of `SearchField`; controlled `value`, `onClear` empties it; name it with `aria-label` or a wrapping `Field`. */
export type SearchFieldProps = Omit<TextFieldProps, "type" | "leading" | "trailing" | "prefix" | "suffix" | "value"> & {
  value: string;
  clearLabel?: string;
  /** Leading magnifier: `sm` subtle (default, compact in-form filters) or `md` muted (the header search). */
  glyph?: "sm" | "md";
} & (
    | { clearable?: true; onClear: () => void }
    | { /** The trailing X shows even when empty: a close-and-clear control (`ExpandingSearch` "Fechar busca"). */ clearable: "always"; onClear: () => void }
    | { /** No inner clear button: the caller owns one close/clear control next to the field. */ clearable: false; onClear?: never }
  );

/** Search input: leading magnifier, and a clear button while it holds text (always with `clearable="always"`, never with `false`). */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { value, onClear, clearable = true, clearLabel = "Limpar busca", glyph = "sm", inputSize = "sm", inputClassName, ...rest },
  ref,
) {
  return (
    <TextField
      {...rest}
      ref={ref}
      type="search"
      value={value}
      inputSize={inputSize}
      inputClassName={cn("[&::-webkit-search-cancel-button]:appearance-none", inputClassName)}
      leading={<Icon as={Search} size={glyph} tone={glyph === "md" ? "muted" : "subtle"} />}
      trailing={
        onClear && (clearable === "always" || (clearable && value.length > 0)) ? (
          <IconButton icon={Close} label={clearLabel} size="icon-xs" onClick={onClear} className="-my-1 -mr-1" />
        ) : null
      }
    />
  );
});
